// Async key-value wrapper around IndexedDB, used as a drop-in replacement
// for sessionStorage specifically for the undo/redo "-history" keys (much
// larger quota than sessionStorage). Values are stored/read as plain strings,
// same as sessionStorage, so callers keep doing JSON.stringify/JSON.parse
// themselves -- only the storage backend changes.

const DB_NAME = 'inkox_designer_history';
const DB_VERSION = 1;
const STORE_NAME = 'history';

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
};

export const idbSetItem = async (key, value) => {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error(`Failed to save (IndexedDB) for ${key}:`, error);
  }
};

export const idbGetItem = async (key) => {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error(`Failed to load (IndexedDB) for ${key}:`, error);
    return null;
  }
};

export const idbRemoveItem = async (key) => {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch (error) {
    console.error(`Failed to remove (IndexedDB) for ${key}:`, error);
  }
};
